import type { ReferrerSummary } from "@redotech/admin-sdk/rpc/schema/referrers/shared";
import { Avatar } from "@redotech/arbiter/avatars/avatar";
import { Flex } from "@redotech/arbiter/flex";
import type { RedoTableColumn } from "@redotech/arbiter/tables/redo-table";
import { RedoTableTextCell } from "@redotech/arbiter/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/arbiter/tables/redo-table-header-cells";

import { formatLastActivity } from "../../onboardings/common/utils";
import { ReferrerStatusBadge } from "./referrer-status-badge";

const referrerTypeLabels: Record<string, string> = {
  individual: "Individual",
  app: "App",
  agency: "Agency",
};

/**
 * Column layout for the referrers list view. Definitions are static —
 * they don't depend on page state — so they live in a module-level const
 * rather than being recomputed via useMemo on every render.
 */
export const REFERRERS_COLUMNS: RedoTableColumn<ReferrerSummary>[] = [
  {
    key: "email",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Email" />,
    renderNormalCell: ({ row }) => <RedoTableTextCell text={row.email} />,
  },
  {
    key: "name",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Name" />,
    renderNormalCell: ({ row }) => (
      <Flex align="center" gap="sm">
        <Avatar
          alt={`${row.name} profile image`}
          imageUrl={row.profileImageUrl}
          name={row.name}
          size="small"
        />
        <RedoTableTextCell text={row.name} />
      </Flex>
    ),
  },
  {
    key: "referralCode",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Referral code" />
    ),
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={row.referralCode} />
    ),
  },
  {
    key: "referrerType",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Type" />,
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell
        text={referrerTypeLabels[row.referrerType] ?? row.referrerType}
      />
    ),
  },
  {
    key: "status",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Status" />,
    renderNormalCell: ({ row }) => <ReferrerStatusBadge status={row.status} />,
  },
  {
    key: "installCount",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Installs" />
    ),
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={row.installCount.toLocaleString()} />
    ),
  },
  {
    key: "createdBy",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Created by" />
    ),
    renderNormalCell: ({ row }) => <RedoTableTextCell text={row.createdBy} />,
  },
  {
    key: "createdAt",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Created" />
    ),
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={formatLastActivity(new Date(row.createdAt))} />
    ),
  },
];
